import { useState, Fragment, useEffect } from "react";
import SearchBar from "../../Common/searchBar";
import ProjectRow from "./doiRow";
import SideNavigation from "../../Common/sideNavigation";

import { Auth } from "../../../../Common/lib/auth";
import { API, apiClient } from "../../../../Common/lib/api";
import ResponseNotification from "../../UserManagement/Modals/responseNotification";

import { AdminGetUser } from '../../../../Common/lib/Admin/AdminAPI';
import sendEmail from '../../../../Common/email/sendEmail';

import Pagination from '../../Common/pagination';

/**
 * Returns the DOI Requests page component.
 *
 * @returns The DOI Requests page component.
 */
const DOIRequestTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [noSearchResults, setNoSearchResults] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [projectPerPage, setProjectPerPage] = useState(6);

  const [notificationShow, setNotificationShow] = useState(false);
  const [notificationPurpose, setNotificationPurpose] = useState("");

  const [users, setUsers] = useState([]);

  const [projects, setProjects] = useState([]);
  const [projectCount, setProjectCount] = useState(0);

  const [notification, setNotification] = useState({
    status: false,
    title: "",
    message: "",
  });

  /**
   * Gets all DOI Requests
   */
  const getProjects = async () => {
    setNoSearchResults(false);
    const params = {
      ordering: "-createdAt",
      status: "pending",
      offset: currentPage * projectPerPage,
      limit: projectPerPage,
    };

    if (searchTerm.length > 0) {
      params.q = searchTerm;
    }

    try {
      const result = await API.doiRequests.list(params);
      const allItems = result.data.listDOIRequests.items || [];
      // Only show pending or unset status to avoid resurfacing approved/rejected requests after reload
      const items = allItems.filter(item => item.status === "pending" || !item.status);
      const total = items.length;

      if (total === 0) {
        setNoSearchResults(true);
      } else {
        setProjects(items);
        setProjectCount(total);
      }

      if (users != []) {
        getUsers();
      }
    } catch (err) {
       // Error handled silently
    }
  };

  useEffect(() => {
    getProjects();
    setProjectCount(0);
  }, [currentPage]);

  /**
   * Retrieves users.
   */
  const getUsers = async () => {
    try {
      const response = await apiClient.get('/admin/users/');
      setPageLoading(false);
      setUsers(response.data.Users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setPageLoading(false);
      setUsers([]);
    }
  };

  /**
   * Returns a date string into a formatted MM/dd/YYYY date.
   *
   * @param {String|Integer} date - ISO date string or Unix timestamp
   * @returns The formatted MM/dd/YYYY date.
   */
  const formatUNIX = (date) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString("en-US");
    }
    return new Date(date).toLocaleDateString("en-US");
  };


  /**
   * Handles the project search input field.
   *
   * @param {Event Handles} event
   */
  const handleSearch = (event) => {
    setSearchTerm((event.target.value).trim());
  };

  const rejectDOIRequest = async (request) => {
    try {
      setNotificationShow(false);
      setNotification({
        status: false,
        title: "",
        message: "",
      });

      let projectId = request.projectID;
      if (projectId && projectId.startsWith('project-')) {
        const projectQuery = await API.projects.get(projectId, projectId);
        const projectObj = projectQuery.data.getProject;
        projectId = projectObj?.id || projectId.substring(8);
      } else if (projectId && typeof projectId === 'string' && projectId.length > 8) {
        projectId = projectId.substring(8);
      }
      
      await API.projects.update({
        id: projectId,
        DOI: "",
      });

      await API.doiRequests.delete(request.id);

      setProjects((prevState) => prevState.filter((prevRequest) => prevRequest.id !== request.id));
    }
    catch (err) {
      // console.log(err);
    }
  }

  /**
   * Updates the selected Project with the new DOI value.
   */
  const updateProjectDOI = async (request, doi) => {
    setNotificationShow(false);
    setNotification({
      status: false,
      title: "",
      message: "",
    });

    try {
      let projectId = request.projectID;
      if (projectId && projectId.startsWith('project-')) {
        const projectQuery = await API.projects.get(projectId, projectId);
        const projectObj = projectQuery.data.getProject;
        projectId = projectObj?.id || projectId.substring(8);
      } else if (projectId && typeof projectId === 'string' && projectId.length > 8) {
        projectId = projectId.substring(8);
      }
      
      await API.projects.update({
        id: projectId,
        DOI: doi,
      });

      await API.doiRequests.update(request.id, { status: 'approved', doi });

      setProjects((prevState) => prevState.filter((prevRequest) => prevRequest.id !== request.id));
      
      try {
        const result = await AdminGetUser(request.userID);
        let userObject = {
          email: ""
        };

        result.UserAttributes.map(x => {
          userObject[`${x.Name}`] = x.Value;
        });

        if (userObject.email !== "" && userObject.email !== undefined) {
          let projectId = request.projectID;
          if (projectId && projectId.startsWith('project-')) {
            try {
              const projectQuery = await API.projects.get(projectId, projectId);
              const projectObj = projectQuery.data.getProject;
              projectId = projectObj?.id || projectId.substring(8);
            } catch (e) {
              projectId = projectId.substring(8);
            }
          } else {
            projectId = projectId?.substring(8) || projectId;
          }
          let emailSubject = "Project DOI Approved"
          let emailBody = `Hello ${userObject.given_name}, \n\nWe are pleased to inform you that your research project titled "${request.projectTitle}" has been assigned a valid Digital Object Identifier (DOI). \n\nYou can find your research project here: https://database.arcticfocus.org/metadata-display/${projectId}`;
          await sendEmail(userObject.email, emailSubject, emailBody, "Authorized");
        }
      } catch (err) {
        // console.log(err.message);
      }

      setNotificationShow(true);
      setNotificationPurpose("DOI");
      setNotification({
        status: true,
        title: "Project DOI Successfully Updated",
        message: `Project DOI has been set to ${doi}!`
      });
    } catch (error) {
      setNotificationShow(true);
      setNotificationPurpose("DOI");
      setNotification({
        status: false,
        title: "An Error has occurred",
        message: `Project DOI could not be saved!`
      });

      // console.log(error);
    }
  };

  return (
    <>
      <div className="">
        {/* Response Modal of the requests from EDITING the projects DOI Attribute */}
        <ResponseNotification
          as={Fragment}
          show={notificationShow}
          notification={notification}
          setShow={setNotificationShow}
          notificationPurpose={notificationPurpose}
        />

        <div className="py-6 px-3 w-full md:px-5 md:py-12 bg-white border border-gray-200 md:w-full rounded-lg xl:p-12 shadow-sm">

          <div className="mt-2">
          <div className="flex justify-between w-full">
            <h1 className="text-2xl py-2 font-bold leading-tight text-gray-900">
              Project DOI Requests
            </h1>
          </div>
          <SearchBar
            searchPurpose={"doi"}
            searchTerm={searchTerm}
            currentPage={currentPage}
            getProjects={getProjects}
            handleSearch={handleSearch}
            setCurrentPage={setCurrentPage}
          />

            {pageLoading ? (
              <div className="text-base p-40 bg-gray-100 place-content-center">
                <p className="text-center font-medium">
                  Loading
                </p>
              </div>
            ) : (
              <>
                {noSearchResults ? (
                  <div className="text-base p-44 bg-gray-100 place-content-center">
                    <p className="text-center font-medium">
                      No DOI Requests Found!
                    </p>
                  </div>
                ) : (
                  <>

                    <table className="w-full max-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:text-base sm:pl-6">
                            Project Name
                          </th>

                          {window.location.href.includes("doi-requests") &&
                            <>
                              <th
                                scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                              >
                                DOI
                              </th>

                              <th
                                scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                              >
                                Created By
                              </th>
                            </>
                          }

                          <th
                            scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:text-base sm:table-cell"
                          >
                            Date
                          </th>

                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6 w-1/4">
                            <span className="sr-only"></span>
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 bg-white">
                        {projects.map((project, index) => (
                          <ProjectRow
                            key={project.id || project.PK}
                            project={project}
                            formatUNIX={formatUNIX}
                            updateProjectDOI={updateProjectDOI}
                            rejectDOIRequest={rejectDOIRequest}
                            users={users}
                          />
                        ))}
                      </tbody>
                    </table>

                    <Pagination
                      projectsPerPage={projectPerPage}
                      currentPage={currentPage}
                      projectCount={projectCount}
                      setCurrentPage={setCurrentPage}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

export default DOIRequestTable;